/**
 * DeviceRadiosSection -- device detail > Radios (access points only). OP#1760.
 *
 * Per band one row: Channel (auto / fixed), Width, TX power. Each field says where its value
 * comes from -- "Site default (X)" (inherits Wireless > Radio Settings) or "Overridden on this
 * AP" -- with a Revert-to-site action. Width options are the AP's own capabilities (2.4 GHz is
 * always 20/40; 5/6 GHz per the radio's htmode list). A mesh-pinned backhaul band shows channel
 * and width read-only (change them on the Mesh page).
 *
 * Applying is two steps on purpose: Preview (dry run: before/after of the band, side effects)
 * and then Apply, which needs an explicit "~4 min outage on this AP" acknowledgement -- every
 * uCentral push restarts the AP's radios. The push outcome is shown as the device reported it:
 * applied / applied with warnings (+ the warning text) / rejected (nothing saved).
 *
 * Reads are site_operator+; editing is org_admin+ (RoleGate here == RADIO_OVERRIDE_WRITE_ROLE
 * in api/v2/radios.py). A site save never overwrites an override; the site page says so.
 */
import { useEffect, useMemo, useState } from "react";
import {
  useDeviceRadios,
  useSetDeviceRadioOverrides,
  type DeviceRadioBand,
  type RadioField,
  type RadioOverrideResult,
} from "@/lib/api/deviceRadios";
import {
  FIELD_LABEL,
  RADIO_FIELDS,
  RECOMMENDED_2G_WIDTH,
  buildOverrideBody,
  fmtRadioValue,
  initialForm,
  isPinned,
  liveNote,
  outcomeView,
  snapshotLines,
  sourceText,
  touchedFields,
  type BandForm,
  type FieldChoice,
} from "@/lib/radioOverrides";
import { RoleGate } from "@/components/auth/RoleGate";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { ErrorBanner } from "@/components/ui/Page";

/** Role needed to change a radio override (server: RADIO_OVERRIDE_WRITE_ROLE). */
const WRITE_ROLE = "org_admin" as const;

// min-w-0 + max-w-full: a <select> is as wide as its longest option ("Site default (20 MHz)")
// and, as a flex/grid item, refuses to shrink below that -- in the 448px device sheet it ran
// past the sheet's right edge (UI review 2026-10-17, manual screenshot 18-radios). These let it
// shrink to its column; the option text is clipped by the control, never the page.
const INPUT =
  "min-w-0 max-w-full rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink outline-none focus:border-brand";

export function DeviceRadiosSection({ serial }: { serial: string }) {
  const { data, isLoading, error } = useDeviceRadios(serial);
  if (isLoading) return <p className="text-xs text-ink-subtle">Loading radios…</p>;
  if (error) return <ErrorBanner error={error} />;
  if (!data || !data.owgw_available || data.bands.length === 0) {
    return <p className="text-xs text-ink-subtle">No radio configuration available from the gateway for this AP.</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-ink-muted">
        Each field follows the site&apos;s Radio Settings unless this AP overrides it. A site-wide
        save never changes an AP&apos;s own setting.
      </p>
      {data.bands.map((b) => (
        <BandRow key={b.band} serial={serial} band={b} outageMinutes={data.outage_minutes} />
      ))}
    </div>
  );
}

function BandRow({
  serial,
  band,
  outageMinutes,
}: {
  serial: string;
  band: DeviceRadioBand;
  outageMinutes: number;
}) {
  const mutation = useSetDeviceRadioOverrides(serial);
  const [form, setForm] = useState<BandForm>(() => initialForm(band));
  const [preview, setPreview] = useState<RadioOverrideResult | null>(null);
  const [ack, setAck] = useState(false);
  const [result, setResult] = useState<RadioOverrideResult | null>(null);

  // When the server state changes (after an apply / revert) re-seed the form from it.
  const serverKey = JSON.stringify(band.fields);
  useEffect(() => {
    setForm(initialForm(band));
    setPreview(null);
    setAck(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);

  const body = useMemo(() => buildOverrideBody(band, form), [band, form]);
  const dirty = touchedFields(body).length > 0;

  const change = (field: RadioField, choice: FieldChoice) => {
    setForm((f) => ({ ...f, [field]: choice }));
    setPreview(null);
    setAck(false);
    setResult(null);
  };

  const runPreview = () =>
    mutation.mutate(
      { band: band.band, body: { ...body, dry_run: true } },
      { onSuccess: (res) => setPreview(res) },
    );
  const apply = () =>
    mutation.mutate(
      { band: band.band, body: { ...body, confirm_outage: true } },
      {
        onSuccess: (res) => {
          setResult(res);
          setPreview(null);
          setAck(false);
        },
      },
    );

  const needsOutage = preview?.preview.changed ?? false;
  const outcome = result ? outcomeView(result) : null;

  return (
    <section className="md-card p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="font-medium text-ink">{band.label}</span>
        <span className="text-[11px] text-ink-subtle">
          {band.mesh_pinned ? "Mesh backhaul: channel + width pinned by the mesh" :
            band.supported === false ? "Not reported by this AP's capabilities" : ""}
        </span>
      </div>

      {/* Columns follow the SHEET's width, not the viewport's: `sm:grid-cols-3` keyed off the
          window, so a 1440px window forced 3 columns into the ~390px sheet body and the selects
          overflowed. auto-fit with a 10rem floor gives 2 columns in the sheet, 3 when wider. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,10rem),1fr))] gap-3">
        {RADIO_FIELDS.map((field) => (
          <FieldCell
            key={field}
            band={band}
            field={field}
            choice={form[field]}
            onChange={(c) => change(field, c)}
          />
        ))}
      </div>

      <RoleGate min={WRITE_ROLE} mode="hide">
        <div className="mt-3 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" disabled={!dirty || mutation.isPending} onClick={runPreview}>
              {mutation.isPending && !preview ? "Previewing…" : "Preview change"}
            </Button>
            {dirty && !preview && (
              <span className="text-[11px] text-ink-subtle">
                {touchedFields(body).map((f) => FIELD_LABEL[f]).join(", ")} changed
              </span>
            )}
          </div>

          {preview && <PreviewBox res={preview} />}

          {preview && needsOutage && (
            <label className="flex items-start gap-2 text-xs text-ink">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
              <span>
                I understand this AP is offline for about {outageMinutes} minutes while it applies
                the new radio settings (~{outageMinutes} min outage on this AP).
              </span>
            </label>
          )}

          {preview && (
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                disabled={mutation.isPending || (needsOutage && !ack)}
                onClick={apply}
              >
                {mutation.isPending ? "Applying…" : needsOutage ? "Apply to this AP" : "Save"}
              </Button>
              <Button variant="secondary" onClick={() => setPreview(null)}>Cancel</Button>
            </div>
          )}

          {outcome && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <StatusPill status={outcome.tone}>{outcome.label}</StatusPill>
              <span className="text-ink-muted">{outcome.text}</span>
            </div>
          )}
          {mutation.isError && (
            <span className="text-xs text-critical">
              {(mutation.error as Error)?.message || "The change failed."}
            </span>
          )}
        </div>
      </RoleGate>
    </section>
  );
}

/** One field: the effective value + its source, and (org_admin+) the editor. */
function FieldCell({
  band,
  field,
  choice,
  onChange,
}: {
  band: DeviceRadioBand;
  field: RadioField;
  choice: FieldChoice;
  onChange: (c: FieldChoice) => void;
}) {
  const f = band.fields[field];
  const pinned = isPinned(band, field);
  const overridden = f.overridden;
  return (
    <div className="flex min-w-0 flex-col gap-1 text-sm">
      <span className="text-xs text-ink-muted">{FIELD_LABEL[field]}</span>
      <span className="font-medium text-ink">
        {fmtRadioValue(field, f.effective, band.schema_default_width)}
      </span>
      <span className={overridden ? "text-[11px] font-medium text-brand" : "text-[11px] text-ink-subtle"}>
        {sourceText(field, f, band.schema_default_width)}
      </span>
      {liveNote(band, field) && (
        <span className="text-[11px] text-ink-subtle">{liveNote(band, field)}</span>
      )}
      <RoleGate min={WRITE_ROLE} mode="hide">
        {pinned ? (
          <span className="text-[11px] text-ink-subtle">Pinned by the mesh</span>
        ) : (
          <div className="flex flex-col gap-1">
            <FieldEditor band={band} field={field} choice={choice} onChange={onChange} />
            {choice !== "inherit" && (
              <button
                type="button"
                className="self-start text-[11px] font-medium text-brand hover:underline"
                onClick={() => onChange("inherit")}
              >
                Revert to site
              </button>
            )}
          </div>
        )}
      </RoleGate>
    </div>
  );
}

/** The select (+ number input for a fixed channel / power). */
function FieldEditor({
  band,
  field,
  choice,
  onChange,
}: {
  band: DeviceRadioBand;
  field: RadioField;
  choice: FieldChoice;
  onChange: (c: FieldChoice) => void;
}) {
  const f = band.fields[field];
  const siteLabel = `Site default (${fmtRadioValue(field, f.site_value, band.schema_default_width)})`;
  const [text, setText] = useState(typeof choice === "number" ? String(choice) : "");
  // Keep the number box in step when the form is re-seeded from the server (after an apply).
  useEffect(() => {
    if (typeof choice === "number") setText(String(choice));
  }, [choice]);

  if (field === "channel_width") {
    const value = choice === "inherit" ? "inherit" : String(choice);
    return (
      <select
        aria-label={`${band.label} width`}
        className={`${INPUT} w-full`}
        value={value}
        onChange={(e) => onChange(e.target.value === "inherit" ? "inherit" : Number(e.target.value))}
      >
        <option value="inherit">{siteLabel}</option>
        {band.widths.map((w) => (
          <option key={w} value={w}>
            {w} MHz{band.band === "2G" && w === RECOMMENDED_2G_WIDTH ? " (recommended)" : ""}
          </option>
        ))}
      </select>
    );
  }

  // Channel / TX power: inherit | auto | fixed number.
  const mode = choice === "inherit" ? "inherit" : choice === "auto" ? "auto" : "fixed";
  const placeholder = field === "channel" ? (band.band === "2G" ? "1-14" : "e.g. 36") : "dBm 1-30";
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <select
        aria-label={`${band.label} ${FIELD_LABEL[field]}`}
        className={`${INPUT} w-full`}
        value={mode}
        onChange={(e) => {
          const m = e.target.value;
          if (m === "inherit") onChange("inherit");
          else if (m === "auto") onChange("auto");
          else onChange(text ? Number(text) : null);
        }}
      >
        <option value="inherit">{siteLabel}</option>
        <option value="auto">Auto</option>
        <option value="fixed">{field === "channel" ? "Fixed channel" : "Fixed power"}</option>
      </select>
      {mode === "fixed" && (
        <input
          inputMode="numeric"
          aria-label={`${band.label} ${FIELD_LABEL[field]} value`}
          className={`${INPUT} w-20 font-mono`}
          value={text}
          placeholder={placeholder}
          onChange={(e) => {
            const v = e.target.value.replace(/[^0-9]/g, "");
            setText(v);
            onChange(v ? Number(v) : null);
          }}
        />
      )}
    </div>
  );
}

/** Dry-run result: the band's before/after and any side effects of the push. */
function PreviewBox({ res }: { res: RadioOverrideResult }) {
  const p = res.preview;
  const lines = snapshotLines(p.before, p.after);
  return (
    <div className="rounded border border-line bg-surface-2 p-2 text-xs">
      <div className="mb-1 font-medium text-ink">
        {p.changed ? "This change will be pushed to the AP" : "No config change -- the AP already runs this"}
      </div>
      <table className="w-full">
        <thead>
          <tr className="text-left text-ink-subtle">
            <th className="font-normal">Key</th>
            <th className="font-normal">Now</th>
            <th className="font-normal">After</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.key} className={l.changed ? "text-ink" : "text-ink-subtle"}>
              <td className="font-mono">{l.key}</td>
              <td className="font-mono">{l.before}</td>
              <td className={l.changed ? "font-mono font-medium" : "font-mono"}>{l.after}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {p.side_effects.map((s) => (
        <p key={s} className="mt-1 text-ink-muted">{s}</p>
      ))}
      {!p.changed && (
        <p className="mt-1 text-ink-muted">
          Saving only records that this AP keeps its own value, so a site-wide save leaves it alone.
        </p>
      )}
    </div>
  );
}
